import { Injectable } from '@angular/core'
import { Route, Router } from '@angular/router'
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs'
import { distinctUntilChanged, map } from 'rxjs/operators'
import { DTOConfigEditorComponent } from '../../../components/dtoconfig-editor/dtoconfig-editor.component'
import { RoleManagementComponent } from '../../../components/role-management/role-management.component'
import { EntityViewComponent } from '../../../modules/dto/components/entity-view/entity-view.component'
import { SelectEntityComponent } from '../../../modules/dto/components/select-entity/select-entity.component'
import EntityUrlsUtils from '../../../utils/entity-urls.util'
import { ScriptActionsComponent } from '../../action-module/components/script-actions/script-actions.component'
import { DashboardComponent } from '../../dashboard/components/dashboard/dashboard.component'
import { DTOEntitiesComponent } from '../../dto/components/dto-entities/dto-entities.component'
import { jmlPermissionGuard } from '../../permissions/guards/jml-permission.guard'
import { SettingsComponent } from '../../settings/components/settings/settings.component'
import { JmlMenuItem } from '../models/JmlMenuItem'
import { MenuConfigService } from './menu-config.service'

/** This service creates dynamic routes from the via be received menu structure. */
@Injectable({
  providedIn: 'root'
})
export class DynamicRoutesService {
  private _dtoTypes: Record<string, { overviewUrl: string; detailView: string }>
  private _$dtoTypes: ReplaySubject<Record<string, { overviewUrl: string; detailView: string }>> = new ReplaySubject<
    Record<string, { overviewUrl: string; detailView: string }>
  >(1)
  private _$initialized: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)
  dynamicRoutesAreInitialized$: Observable<boolean> = this._$initialized.asObservable().pipe(distinctUntilChanged())

  constructor(
    private router: Router,
    private menuConfigService: MenuConfigService
  ) {}

  /** adds dynamic routes by analyzing the dynamic menu structure for the entities. */
  updateRoutes() {
    this._dtoTypes = {}

    this.menuConfigService.getMenuEntries().subscribe((entries) => {
      entries.forEach((entry) => this.addRoutes(entry, ''))
      this._$dtoTypes.next(this._dtoTypes)

      this._$initialized.next(true)
    })
  }

  /** removes routes that are marked with a dynamic-boolean flag
   * (which means that they were created at runtime from backend-data) */
  removeAllDynamicRoutes() {
    this._dtoTypes = {}
    this._$dtoTypes.next(this._dtoTypes)

    const routes = this.router.config
    const blankRoute = routes.find((r) => r.path === '')
    const rootId = routes.indexOf(blankRoute)
    const blankRouteChildren = blankRoute?.children

    if (!blankRouteChildren || blankRouteChildren.length === 0) {
      return
    }

    // this.printPath('', this.router.config);

    const f = blankRouteChildren.filter((c) => !c.data?.dynamic)

    // this.printPath('', f);

    routes[rootId].children = f

    this.router.resetConfig(routes)
    this._$initialized.next(false)
  }

  /** prints routes to console */
  printPath(parent: String, config: Route[]) {
    for (let i = 0; i < config.length; i++) {
      const route = config[i]
      if (route.children) {
        const currentPath = route.path ? parent + '/' + route.path : parent
        this.printPath(currentPath, route.children)
      }
    }
  }

  /** generates target URLs for dynamic entity routing paths. */
  generateUrlForEntityType$(values: { entityType: string; id?: number }): Observable<string> {
    // temporary hack for ticket: #2873 - when ticket is fully implemented, this can be removed.
    const entityType = values.entityType.replace(/Selector$/, '')

    return this._$dtoTypes.pipe(
      distinctUntilChanged(),
      map((entries) => {
        if (!!entries[entityType]) {
          return !!values.id
            ? entries[entityType].detailView.replace(':id', values.id + '')
            : entries[entityType].overviewUrl
        }
        return EntityUrlsUtils.getBaseUrlForEntityType(entityType)
      })
    )
  }

  private addRoute(route: Route): void {
    // Flag dynamically routes in order to remove them on logout
    route.data.dynamic = true
    // Add route as child of LayoutComponent
    this.router.config.find((r) => r.path === '')?.children.push(route)
  }
  /** adds route to dto overview  */
  private addOverviewRoute(dtoType: string, displayName: string, routePrefix: string, icon: string) {
    const route: Route = {
      path: routePrefix + dtoType,
      pathMatch: 'full',
      canActivate: [jmlPermissionGuard],
      component: SelectEntityComponent,
      data: {
        entityType: dtoType,
        displayName,
        icon,
        dynamic: true
      }
    }
    this.addRoute(route)
  }

  /** adds route for dto details view (multi-page) */
  private addDetailRoute(dtoType: string, displayName: string, routePrefix: string, icon: string) {
    const route: Route = {
      path: routePrefix + dtoType + `/:id`,
      pathMatch: 'full',
      canActivate: [jmlPermissionGuard],
      component: EntityViewComponent,
      data: {
        entityType: dtoType,
        displayName,
        icon,
        dynamic: true
      }
    }
    this.addRoute(route)
  }

  private addRoutesToDict(dtoType: string, routePrefix: string) {
    this._dtoTypes[dtoType] = {
      overviewUrl: `/${routePrefix}${dtoType}`,
      detailView: `/${routePrefix}${dtoType}/:id`
    }
  }

  private getComponentByType(type: string) {
    switch (type) {
      case 'ScriptIde':
        return ScriptActionsComponent
      case 'DtoConfigEditor':
        return DTOConfigEditorComponent
      case 'DtoEntities':
        return DTOEntitiesComponent
      case 'RoleManagement':
        return RoleManagementComponent
      case 'Dashboard':
        return DashboardComponent
      case 'UserSettings':
      case 'SystemSettings':
        return SettingsComponent
    }
  }

  private addComponentRoute(item: JmlMenuItem, routePrefix: string) {
    const type = item.type
      .split(/(?=[A-Z])/)
      .map((word) => word.toLocaleLowerCase())
      .join('-')
    const route: Route = {
      path: item.route ? item.route : routePrefix + type,
      pathMatch: 'full',
      canActivate: [jmlPermissionGuard],
      component: this.getComponentByType(item.type),
      data: {
        permissions: [item.securityRight],
        icon: item.icon,
        displayName: item.name
      }
    }

    if (item.type === 'UserSettings') route.data['userSettingsOnly'] = true

    this.router.config.find((r) => r.path === '')?.children.push(route)
  }

  /** generates dynamic routes and adds them to router. */
  private addRoutes(item: JmlMenuItem, routePrefix: string) {
    if (item.dtoType && !item.children) {
      this.addOverviewRoute(item.dtoType, item.name, routePrefix, item.icon)
      this.addDetailRoute(item.dtoType, item.name, routePrefix, item.icon)
      this.addRoutesToDict(item.dtoType, routePrefix)
    } else if (item.type && !item.children) {
      this.addComponentRoute(item, routePrefix)

      if (item.type === 'RoleManagement') {
        this._dtoTypes['SecurityRole'] = {
          overviewUrl: `/${routePrefix}${item.route || 'role-management'}`,
          detailView: `/${routePrefix}${item.route || 'role-management'}/:id`
        }
      }
    } else {
      item.children?.forEach((child) => this.addRoutes(child, routePrefix + (item.route ? item.route + '/' : '')))
    }
  }
}
